import React from "react";
import { motion, AnimatePresence } from 'framer-motion';
import { AudioFill, EditSFill  } from 'antd-mobile-icons'
import { useI18n } from '@/hooks/useI18n';

interface InputModeButtonProps {
  inputMode: "voice" | "text";
  isRecording: boolean;
  disabled?: boolean;
  onToggleMode: () => void;
  onCancelRecording: () => void;
}

const InputModeButton: React.FC<InputModeButtonProps> = ({
  inputMode,
  isRecording,
  disabled = false,
  onToggleMode,
  onCancelRecording,
}) => {
  const { t } = useI18n();
  const handleClick = () => {
    if (isRecording) {
      onCancelRecording();
    } else {
      onToggleMode();
    }
  };

  const getButtonContent = () => {
    if (isRecording) {
      return {
        icon: "✕",
        className: "bg-red-100 hover:bg-red-200 text-red-600",
        title: t('ai.chat.cancelRecording', '取消录音'),
      };
    }

    if (inputMode === "voice") {
      return {
        icon: <EditSFill fontSize={18}/>,
        className: "bg-blue-100 hover:bg-blue-200 text-blue-600",
        title: t('ai.chat.switchToTextInput', '切换到文字输入'),
      };
    }

    return {
      icon: <AudioFill fontSize={18}/>,
      className: "bg-gray-100 hover:bg-gray-200 text-gray-600",
      title: t('ai.chat.switchToVoiceInput', '切换到语音输入'),
    };
  };

  const { icon, className, title } = getButtonContent();

  return (
    <motion.button
      onClick={handleClick}
      disabled={disabled}
      title={title}
      className='w-10 h-10 bg-[rgba(152,119,255,0.2)] rounded-full p-1'
      whileHover={{ 
        scale: 1.1,
        boxShadow: "0 4px 20px rgba(78, 143, 253, 0.3)"
      }}
      whileTap={{ scale: 0.95 }}
      animate={{
        backgroundColor: isRecording 
          ? 'rgba(239, 68, 68, 0.2)' 
          : 'rgba(152, 119, 255, 0.2)'
      }}
      transition={{ 
        type: "spring", 
        stiffness: 400, 
        damping: 17,
        backgroundColor: {
          duration: 0.3,
          ease: "easeInOut"
        }
      }}
    >
      <motion.div 
        className='w-full h-full flex items-center justify-center rounded-full'
        animate={{
          background: isRecording
            ? 'linear-gradient(90deg, #ef4444, #dc2626)'
            : 'linear-gradient(90deg, #4E8FFD, #A785F5)'
        }}
        transition={{
          duration: 0.3,
          ease: "easeInOut"
        }}
      >
        <AnimatePresence mode="wait">
          <motion.span 
            key={`${inputMode}-${isRecording}`}
            className='text-white text-sm'
            initial={{ opacity: 0, scale: 0.8, rotate: -90 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            exit={{ opacity: 0, scale: 0.8, rotate: 90 }}
            transition={{ 
              duration: 0.2,
              ease: "easeOut"
            }}
          >
            {icon}
          </motion.span>
        </AnimatePresence>
      </motion.div>
    </motion.button>
  );
};

export default InputModeButton;
